import React from 'react';
import {Alert, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useFormikContext} from "formik";
import {ContaminationDeclarationFormValues, YesNoField} from "../../utils/contaminationDeclaration/fields";
import QuestionRow from "./QuestionRow";

interface Props {
    index: YesNoField;
    label: string;
    hint?: string | null;
    /** Flagged by the backend's validity as required and unanswered. */
    missing?: boolean;
    disabled?: boolean;
    /** Safety notice to show under the question when it applies. */
    warning?: string | null;
}

/**
 * Tri-state yes/no question: nothing is selected until the user answers, and
 * an answer can be corrected but never removed (the backend rejects `null`).
 * One row of the section's question list; the toggle is the app's
 * ToggleButtonGroup (SystemFileManagement) with the app's non-uppercase
 * button casing.
 */
const YesNoToggle = ({index, label, hint, missing, disabled, warning}: Props) => {
    const {t} = useTranslation();
    const {values, setFieldValue} = useFormikContext<ContaminationDeclarationFormValues>();
    const value = values[index];
    const selected = value === null || value === undefined ? null : value ? 'yes' : 'no';

    return (
        <QuestionRow
            label={label}
            hint={hint}
            missing={missing && selected === null}
            testId={`doc-yesno-${index}`}
            control={
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={selected}
                    disabled={disabled}
                    onChange={(_, next: 'yes' | 'no' | null) => {
                        // Clicking the selected button again yields null — keep the answer.
                        if (next !== null) setFieldValue(index, next === 'yes');
                    }}
                    aria-label={label}
                    sx={{"& .MuiToggleButton-root": {textTransform: "none", px: "20px", minWidth: 64}}}
                >
                    <ToggleButton value="yes" data-testid={`doc-yesno-${index}-yes`}>{t('Yes')}</ToggleButton>
                    <ToggleButton value="no" data-testid={`doc-yesno-${index}-no`}>{t('No')}</ToggleButton>
                </ToggleButtonGroup>
            }
        >
            {warning && (
                <Alert severity="warning" sx={{mt: 1.5}} data-testid={`doc-warning-${index}`}>{warning}</Alert>
            )}
        </QuestionRow>
    );
};

export default YesNoToggle;
